import {Component, forwardRef, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {PermissionDto, PermissionService} from '../../../_services/configuration-services';
import {Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {TranslateUtils} from '../../translate-utils';

@Component({
    selector: 'app-permission-management',
    templateUrl: './permission-management.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PermissionManagementComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PermissionManagementComponent implements OnInit, OnDestroy, ControlValueAccessor {

    permissions: PermissionDto[] = [];
    permissionCheckboxes: boolean[] = [];
    loaded = false;
    isDisabled = false;
    crtLang = TranslateUtils.defaultLanguage;

    private pendingValue: PermissionDto[] | null = null;
    private onChange: (value: PermissionDto[] | null) => void = () => {};
    private onTouched: () => void = () => {};
    private langChangeSubscription: Subscription;

    constructor(
        private readonly permissionService: PermissionService,
        private readonly translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.permissionService.getAllPermissions().pipe().subscribe(permissions => {
            this.permissions = permissions;
            this.permissionCheckboxes = permissions.map(() => false);

            if (this.pendingValue !== null) {
                this.applyValue(this.pendingValue);
                this.pendingValue = undefined;
            }

            this.loaded = true;
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe();
    }

    // ControlValueAccessor

    writeValue(value: PermissionDto[] | null): void {
        if (!this.loaded) {
            this.pendingValue = value;
            return;
        }
        this.applyValue(value);
    }

    registerOnChange(fn: (value: PermissionDto[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    // Event handlers

    onPermissionChange(): void {
        this.emitValue();
        this.onTouched();
    }

    // Private helpers

    private applyValue(value: PermissionDto[] | null): void {
        if (value === null || value.length === 0) {
            this.permissionCheckboxes = this.permissions.map(() => false);
        } else {
            this.permissionCheckboxes = this.permissions.map(permission =>
                value.some(selected => selected.id === permission.id)
            );
        }
    }

    private emitValue(): void {
        this.onChange(this.permissions.filter((_, i) => this.permissionCheckboxes[i]));
    }
}
